/**
 * Internal hold (GDN-4065): the view model and the words the review browser
 * shows for it.
 *
 * A hold keeps a staged row out of every commit until staff release it, and it
 * never reaches the practice. It is mutually exclusive with the org-review flag:
 * the server REFUSES holding a flagged row and flagging a held row, and never
 * lets one rewrite the other. So the UI offers the refused action disabled, with
 * the reason beside it, rather than hiding it — an action that disappears
 * teaches staff the product can't do it, a refusal with a reason teaches them
 * the rule.
 *
 * Every refusal MUST say which row, why, and the exact next step (the condition
 * Marcus attached to "refuse, don't auto-clear"). On a row, "which row" is the
 * row the sentence sits on; in a bulk result it comes from the server's own
 * per-row message, which names the row.
 */

/** One row's persisted hold, as the browse returns it. */
export interface InternalHold {
  /**
   * Required and non-blank server-side; operational, never clinical. Empty
   * only if a row predates that rule — shown as "no reason recorded", and
   * never prefilled into an edit as if it were one.
   */
  reason: string;
  /**
   * The staff member (or agent) who placed it, as a display name. Null when the
   * server could not resolve one — shown as no name, never as "you".
   */
  byName?: string | null;
  at?: string | null;
}

/** Server-driven hold filter, parallel to the org-review filter. */
export type HoldFilterMode = 'all' | 'held' | 'not_held';

export type HoldAction = 'hold' | 'release';

/**
 * What a hold or release request did, per the server's per-row outcomes.
 * `refused` carries the server's message verbatim: it names the row and the
 * next step, which the UI can't reconstruct for a row it may not have loaded.
 */
export interface HoldActionResult {
  action: HoldAction;
  /** Rows newly held, or released. */
  changed: number;
  /**
   * Held rows whose reason this request changed (outcome `reason_updated`).
   * Counted apart from `changed`: re-holding an already-held row is not a new
   * hold, and "Held 3 rows" must not count it as one.
   */
  reasonUpdated?: number;
  /** Rows already in the requested state (an idempotent retry). */
  unchanged: number;
  refused: { id: string; message: string }[];
}

/** The server's limit on a hold reason (`maxLength` on the hold request). */
export const HOLD_REASON_MAX = 500;

/** Holding a flagged row — shown beside the disabled "Hold internally". */
export const HOLD_REFUSED_FLAGGED =
  'Flagged for practice review. Clear the flag to hold this row internally.';

/** "Held internally by Dana Ruiz: waiting on the date fix." */
export function heldAttribution(hold: InternalHold): string {
  const by = hold.byName?.trim();
  const reason = hold.reason.trim();
  const who = `Held internally${by ? ` by ${by}` : ''}`;
  return reason ? `${who}: ${endSentence(reason)}` : `${who} (no reason recorded).`;
}

/**
 * Shown on a held row, beside the disabled "Flag for org review". A held row
 * also blocks resolve, match, skip and commit until it is released, so the next
 * step names all of them, not only the flag.
 */
export function heldRowMessage(hold: InternalHold): string {
  return `${heldAttribution(hold)} Release the hold to flag, resolve or commit this row.`;
}

/** One line over a bulk result: what changed, then what didn't and why. */
export function holdResultSummary(result: HoldActionResult): string {
  const { action, changed, unchanged, refused } = result;
  const reasonUpdated = result.reasonUpdated ?? 0;
  const verb = action === 'hold' ? 'Held' : 'Released';
  const already = action === 'hold' ? 'already held' : 'not held';
  const parts: string[] = [];
  if (changed > 0) parts.push(`${verb} ${rows(changed)}.`);
  if (reasonUpdated > 0) {
    parts.push(`Changed the reason on ${rows(reasonUpdated)} already held.`);
  }
  if (parts.length === 0) {
    parts.push(`No rows were ${action === 'hold' ? 'held' : 'released'}.`);
  }
  if (unchanged > 0) {
    parts.push(`${rows(unchanged)} ${unchanged === 1 ? 'was' : 'were'} ${already}.`);
  }
  if (refused.length > 0) {
    parts.push(`${rows(refused.length)} ${refused.length === 1 ? 'was' : 'were'} refused:`);
  }
  return parts.join(' ');
}

/** One held row from a commit's results: identity only, never content. */
export interface CommitHeldRow {
  import_resource_id: string;
  resource_type: string;
  source_key: string;
}

/** What a finished commit left out because of holds (GDN-4065). */
export interface CommitHeldResult {
  count: number;
  rows: CommitHeldRow[];
  dependencyCount: number;
  dependencyRows: CommitHeldRow[];
}

/** "Commit complete: …" — held rows named apart from skipped and failed. */
export function commitCompleteMessage(
  counts: { imported?: number; failed?: number; skipped?: number },
  held?: CommitHeldResult,
): string {
  const heldCount = held?.count ?? 0;
  const blocked = held?.dependencyCount ?? 0;
  // Rows blocked by a held parent are counted as failed by the server; say
  // which of the failures they are, so none reads as a mystery.
  const failed = `${counts.failed ?? 0} failed${blocked > 0 ? ` (${blocked} blocked by a held parent)` : ''}`;
  const heldPart = heldCount > 0 ? `, ${heldCount.toLocaleString()} held` : '';
  return `Commit complete: ${counts.imported ?? 0} committed, ${failed}, ${counts.skipped ?? 0} skipped${heldPart}.`;
}

/** The heading over one list in the commit's held summary. */
export function commitHeldHeading(kind: 'held' | 'blocked', count: number): string {
  return kind === 'held'
    ? `${rows(count)} ${count === 1 ? 'was' : 'were'} held internally and left out of this commit:`
    : `${rows(count)} ${count === 1 ? 'was' : 'were'} not committed because a parent ${count === 1 ? 'it depends' : 'they depend'} on is held:`;
}

/** The line under a capped list: the counts are complete, the lists are not. */
export function commitHeldMore(listed: number, count: number): string | null {
  return count > listed ? `And ${(count - listed).toLocaleString()} more not listed here.` : null;
}

/**
 * Snap a filter whose segment emptied back to 'all' — same rule as the
 * org-review filter. 'not_held' with nothing held yet stays put: then it is the
 * whole import, which is a real view.
 */
export function reconcileHoldFilter(
  mode: HoldFilterMode,
  heldTotal: number,
  notHeldTotal: number,
): HoldFilterMode {
  if (mode === 'held' && heldTotal === 0) return 'all';
  if (mode === 'not_held' && notHeldTotal === 0) return 'all';
  return mode;
}

function rows(n: number): string {
  return `${n.toLocaleString()} ${n === 1 ? 'row' : 'rows'}`;
}

function endSentence(text: string): string {
  return /[.!?]$/.test(text) ? text : `${text}.`;
}
